import { TriggerType, events, defines, ThreeViewBase, ItemManager, CanvasInfo, CameraControls, PosOrigin, WalkThroughAnimation } from ".";

import * as digifie from "../../../digifie";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

/* Camera
 *  -THREE.Camera - camera body
 *  -CameraControl - event handler
 *  -CameraNavigators  - UIs
 */
/* Camera
 * 3Dカメラ基本クラス.
 */
export class Camera {
    private _instance: THREE.Camera;
    public get me(): THREE.Camera { return this._instance; }
    public set me(i: THREE.Camera) { this._instance = i; }

    private _cameras: Cameras;

    public get position(): THREE.Vector3 { return this.me.position; }
    public set position(pos: THREE.Vector3) { this.me.position.set(pos.x, pos.y, pos.z); }
    public get visible(): boolean { return this.me.visible; }
    public set visible(value: boolean) { this.me.visible = value; }
    public get lookAt(): THREE.Vector3 {
        const vec = new THREE.Vector3;
        this.me.getWorldDirection(vec);
        return vec;
    }
    public set lookAt(pos: THREE.Vector3) { this.me.lookAt(pos); }
    public get uiEnabled(): boolean { return this.controllable() ? this.control.uiEnabled : false; }
    public set uiEnabled(value: boolean) { if (this.controllable()) this.control.uiEnabled = value; }
    public get cameras(): Cameras { return this._cameras; }
    public set cameras(cam: Cameras) { this._cameras = cam; }
    public home() { /* do nothing */ }
    public command(val: number, param?: {}) { /* do nothing */ }

    // Settings
    private _settings: any;
    public getSetting(label: string): any {
        if (this._settings != undefined) return this._settings[label];
    }
    public setSetting(label: string, setting: any) {
        if (this._settings == undefined) this._settings = {}
        this._settings[label] = setting;
    }

    // Camera Event Handler
    private _control: CameraControl;
    public controllable(): boolean { return this._control !== undefined; }
    public get control(): CameraControl { return this._control; }
    public set control(ctrl: CameraControl) { this._control = ctrl; }

    // Camera Controller UIs
    private _navigators: CameraNavigator[];
    public navigatable(): boolean { return this._navigators !== undefined; }
    public get navigators(): CameraNavigator[] { return this._navigators; }
    public set navigators(navs: CameraNavigator[]) { this._navigators = navs; }
    public addNavigator(nav: CameraNavigator): CameraNavigator {
        if (this._navigators == undefined) this._navigators = [];
        this._navigators.push(nav);
        return nav;
    }

    public getNavigator(type: any, initParam?: any): CameraNavigator {
        for (let i = 0; i < this._navigators.length; i++) {
            if (this._navigators[i] instanceof type)
                return this._navigators[i];
        }
        if (initParam != undefined) return this.addNavigator(new type(initParam));
        return this.addNavigator(new type);
    }

    public reset(options: defines.Options) {
        if (this.controllable()) {
            this.control.reset(options);
        }
        if (this.navigatable()) {
            for (let j = 0; j < this.navigators.length; j++) {
                this.navigators[j].reset(options);
            }
        }
    }

    public update(items: ItemManager, timeStamp: number) {
        if (this.controllable()) {
            this.control.update(this.cameras, this, items, timeStamp);
        }
        if (this.navigatable()) {
            for (let j = 0; j < this.navigators.length; j++) {
                this.navigators[j].update(this.cameras, this, items, timeStamp);
            }
        }
    }

    public notify(message: string, param: {}) {
        if (this.controllable()) {
            this.control.notify(message, param);
        }
        if (this.navigatable()) {
            for (let j = 0; j < this.navigators.length; j++) {
                this.navigators[j].notify(message, param);
            }
        }
    }
    public canvasSizeChanged() {
        // if (!(this.me instanceof THREE.PerspectiveCamera)) return;

        // let size = CanvasInfo.get().size;
        // let pers_cam = <THREE.PerspectiveCamera>(this.me);
        // pers_cam.aspect = size.x / size.y;
        // pers_cam.updateProjectionMatrix();
    }

    //コンストラクタ
    constructor(cams?: Cameras, cam?: THREE.Camera) {
        if (cams != undefined)
            this._cameras = cams;
        if (cam != undefined)
            this.me = cam;
    }
}


/* Cameras
 *　カメラマネージャ.
 */
export class Cameras {
    private _cameras: Camera[];
    protected _current: number;

    public getCamera(index: number): Camera { return this._cameras[index]; }

    public get camera(): Camera {
        if (this._current >= 0)
            return this._cameras[this._current];
        return undefined;
    }

    private _eventDispatcher: events.EventDispatcher; // introduce as member
    public get eventDispatcher() { return this._eventDispatcher; }

    constructor(eventDispatcher?: events.EventDispatcher) {
        this._cameras = [];
        this._current = -1;
        this._eventDispatcher = eventDispatcher;
    }

    /*
     * カメラの追加削除.
     */
    public add(camera: Camera, act: boolean) {
        this._cameras.push(camera);
        if (act) this.no = this._cameras.length - 1;
    }
    public remove(camera: Camera) {
        const index = this.find(camera);
        if (index >= 0) this._cameras.splice(index, 1);
        if (index == this._current) this._current = Math.max(index - 1, 0);
    }
    public find(camera: Camera): number {
        for (let i = 0; i < this._cameras.length; i++)
            if (this._cameras[i] == camera) return i;
        return -1;
    }
    public _find(camera: THREE.Camera): number {
        for (let i = 0; i < this._cameras.length; i++)
            if (this._cameras[i].me == camera) return i;
        return -1;
    }
    public reset(options: defines.Options): void {
        if (options.is(defines.ResetOptions.deleteCameras, true)) {
            this._cameras.splice(0, this._cameras.length);
        }
        else {
            for (let i = 0; i < this._cameras.length; i++) {
                this._cameras[i].reset(options);
            }
        }
        this._current = 0;
    }

    //オービットモード（外観)
    public isOrbitCameraMode(): boolean {
        return this._current == defines.CameraDefs.orbitCamNo;
    }
    //ウォークスルーモード(内観)
    public isWalkThroughCameraMode(): boolean {
        return this._current == defines.CameraDefs.wlkthCamNo;
    }

    public home() { if (this.camera != undefined) this.camera.home(); }
    public command(val: number, param?: {}) { if (this.camera != undefined) this.camera.command(val, param); }

    public notifyAll(message: string, param: {}): void {
        for (let i = 0; i < this._cameras.length; i++) {
            this._cameras[i].notify(message, param);
        }
    }

    /*
     * カメラ番号.
     * property のsuper呼び出しができないので、2段階で実装.
     */
    public setCameraNo(index: number): void {
        if (index < this._cameras.length) {
            for (let i = 0; i < this._cameras.length; i++) {
                const camera = this._cameras[i];
                const enable = (i === index);
                camera.visible = enable;
                if (camera.controllable()) {
                    camera.control.enabled = enable;
                }
                if (camera.navigatable()) {
                    for (let j = 0; j < camera.navigators.length; j++) {
                        camera.navigators[j].enabled = enable;
                    }
                }
            }
            this._current = index;

            // notify concerned objects.
            this.eventDispatcher.dispatchEvent(new events.Event(defines.EventNames.cameraChanged, index));
        }
    }

    public get no(): number { return this._current; }
    public set no(index: number) { this.setCameraNo(index); }

    /*　update
     * カメラ状態のアップデート.
     */
    public update(items: ItemManager, timeStamp: number) {
        this.camera.update(items, timeStamp);

        const isDebug = false;
        if (isDebug) {
            this.comment();
        }
    }

    private beforePos = new THREE.Vector3();
    private beforeDirection = new THREE.Vector3();
    private comment() {
        if (this.beforePos.clone().equals(this.camera.me.position.clone())
                && this.beforeDirection.clone().equals(this.camera.me.getWorldDirection(new THREE.Vector3()).clone())) return;

        new Promise(() => {
            console.log(this.camera.me.position.clone());
            console.log(this.camera.me.getWorldDirection(new THREE.Vector3()).clone());
        });

        this.beforePos = this.camera.me.position.clone();
        this.camera.me.getWorldDirection(this.beforeDirection);
    }

    /*
     * イベントコールバック.
     */
    public canvasSizeChanged(): void {
        for (let i = 0; i < this._cameras.length; i++) {
            this._cameras[i].canvasSizeChanged();
        }
    }

    /*
     * 現在の3Dビューにあるカメラを登録.
     */
    public parse(view: ThreeViewBase): void {
        for (let i = 0; i < view.scene.me.children.length; i++) {
            let ob: THREE.Camera = <THREE.Camera>(view.scene.me.children[i]);
            if (this._find(ob) >= 0) continue;
            if (ob.type == "PerspectiveCamera") {
                const cam: Camera = new Camera(view.cameras, <THREE.PerspectiveCamera>(ob));

                cam.control = view.createCameraControl(cam);
                this._cameras.push(cam);
            }
            else if (ob.type == "OrthographicCamera") {
                const c: THREE.OrthographicCamera = <THREE.OrthographicCamera>(ob);
                c.near = 0.1;
                c.far = 1000;
                const cam: Camera = new Camera(view.cameras, c);

                cam.control = view.createCameraControl(cam);
                this._cameras.push(cam);
            }
        }
        if (this._cameras.length > 0) {
            this._current = 0;
        }
        else {
            view.createCameras();
        }
    }
}

/* CameraControl
 *　カメラコントローラ.
 */
export enum CameraControlType {
    Orbit,
    WalkThrough,
    Object,
}

export class CameraControl {
    protected _type: CameraControlType;
    private _subCtrl: CameraControl;

    constructor() {
    }

    public get enabled(): boolean { return false; }
    public set enabled(value: boolean) {/* do nothing */ }
    public set target(pos: THREE.Vector3) {/* do nothing */ }
    public reset(options: defines.Options) { /*do nothing*/ }
    public update(cameras: Cameras, camera: Camera, items: ItemManager, timeStamp: number) {
        if (this._subCtrl != undefined) this._subCtrl.update(cameras, camera, items, timeStamp);
    }
    public notify(message: string, param: {}) { /* do nothing */ }
    public get uiEnabled(): boolean { return false; }
    public set uiEnabled(value: boolean) { /* do nothing*/ }
    public dispose() { this.subCtrl = undefined; }
    public get subCtrl(): CameraControl { return this._subCtrl; }
    public set subCtrl(ctrl: CameraControl) {
        if (this._subCtrl != undefined) this._subCtrl.dispose();
        this._subCtrl = ctrl;
    }
    public command(val: number, param?: {}) { /* do nothing */ }
}

/* CameraNavigator
 *　カメラナビゲーションUI
 */
export class CameraNavigator {
    constructor(eventDispatcher?: events.EventDispatcher) {
    }
    public get enabled(): boolean { return false; }
    public set enabled(value: boolean) {/* do nothing */ }
    public reset(options: defines.Options) { /*do nothing*/ }
    public update(cameras: Cameras, camera: Camera, items: ItemManager, timeStamp: number) { /* do nothing */ }
    public notify(message: string, param: {}) { /* do nothing */ }
}

// 基本クラス終わり.

// Camera 派生クラス.
/* PerspectiveCamera
 * Perspectiveカメラクラス.
 */
export class PerspectiveCamera extends Camera {
    constructor(fov: number, aspect: number, near: number, far: number, cams?: Cameras) {
        super(cams);

        this.me = new THREE.PerspectiveCamera(fov, aspect, near, far);
        this.me.visible = false;
    }

    public command(val: number, param?: {}): void {
        if (!this.controllable()) return;
        this.control.command(val, param);
    }

    public canvasSizeChanged(): void {
        const size = CanvasInfo.get().size;
        const pers_cam = <THREE.PerspectiveCamera>(this.me);
        pers_cam.aspect = size.x / size.y;
        pers_cam.updateProjectionMatrix();
    }
}

// Camera 派生クラス.
/* DefaultCamera
 * テスト用カメラクラス.
 */
export class DefaultCamera extends PerspectiveCamera {
    constructor(cams?: Cameras) {
        // カメラを作成.
        const size = CanvasInfo.get().size;
        super(75, size.x / size.y, 0.1, 1000, cams);
        this.visible = true;

        // カメラ位置を設定.
        this.position = new THREE.Vector3(0, 0.5, 3);
    }
}

// Camera 派生クラス.
/* CaptureCamera
 */
export class CaptureCamera extends Camera {
    constructor(left: number, right: number, top: number, bottom: number, near: number, far: number, cams?: Cameras) {
        super(cams);

        // カメラを作成.
        this.me = new THREE.OrthographicCamera(left, right, top, bottom, near, far);
        this.me.visible = false;
    }
}

// CameraControl 派生クラス.
/* OrbitCameraControl
 *　オービットカメラコントローラ
 */
export class OrbitCameraControl extends CameraControl {
    _instance: OrbitControls;
    public get me(): OrbitControls { return this._instance; }
    public set me(i: OrbitControls) { this._instance = i; }

    constructor(camera: Camera, container: HTMLElement, eventDispatcher?: events.EventDispatcher) {
        super();

        this.me = new OrbitControls(camera.me, container);
        this.enabled = false;
        this._type = CameraControlType.Orbit;
    }
    public get enabled(): boolean { return this.me.enabled; }
    public set enabled(value: boolean) {
        this.me.enabled = value;
        this.me.update();
    }
    public set target(pos: THREE.Vector3) {
        this.me.target = pos;
    }
    public update(cameras: Cameras, camera: Camera, items: ItemManager, timeStamp: number) {
        this.me.update();
        super.update(cameras, camera, items, timeStamp);
    }
    public get uiEnabled(): boolean { return this.me.enabled; }
    public set uiEnabled(value: boolean) { this.me.enabled = value; }

    public setEnabled(flag: boolean) {
        this.me.enabled = flag;
    }
}

// CameraControl 派生クラス.
/* WalkThroughCameraControl
 *　ウォークスルーカメラコントローラ
 */
export class WalkThroughCameraControl extends CameraControl {
    _instance: digifie.controls.WalkThroughControl;
    public get me(): digifie.controls.WalkThroughControl { return this._instance; }
    public set me(i: digifie.controls.WalkThroughControl) { this._instance = i; }

    constructor(camera: Camera, container: HTMLElement, eventDispatcher?: events.EventDispatcher) {
        super();

        this.me = new digifie.controls.WalkThroughControl(camera.me, container, eventDispatcher);
        this.enabled = false;
        this._type = CameraControlType.WalkThrough;
    }

    public reset(options: defines.Options) {
        let keepMotion: boolean = options.is(defines.ResetOptions.keepMotion, false);
        if (keepMotion) this.me.resetAngle();
        else this.me.reset();
    }

    public update(cameras: Cameras, camera: Camera, items: ItemManager, timeStamp: number) {
        this.me.update(<any>items.meshs, items.hitMeshs);
        super.update(cameras, camera, items, timeStamp);
    }

    public notify(message: string, param: any) {
        if (message == defines.EventNames.cameraReset) {
            this.me.reset();
        }
        else if (message == defines.EventNames.wtHitCheck) {
            let items: ItemManager = ThreeViewBase.get().itemmgr;
            this.me.hitTest(items.meshs, items.hitMeshs, undefined, false);
        }
    }

    public command(val: number, param?: any): void {
        const scope = this;
        let timeout = 0;
        if ((param != undefined) && (param['timeout'] != undefined)) {
            timeout = param['timeout'];
        }
        switch (val) {
            case defines.Commands.go:
                this.me.forward();
                if (timeout > 0) setTimeout(function () { scope.me.stop() }, timeout);
                break;
            case defines.Commands.back:
                this.me.back();
                if (timeout > 0) setTimeout(function () { scope.me.stop() }, timeout);
                break;
            case defines.Commands.stop:
                this.me.stop();
                break;
        }
    }
}

// CameraControl 派生クラス.
/* MouseControls
 *　オービットカメラコントローラ
 */
export class MouseControls extends CameraControl {
    private _instance: digifie.controls.MouseControls;
    public get me(): digifie.controls.MouseControls { return this._instance; }
    public set me(i: digifie.controls.MouseControls) { this._instance = i; }

    private _updateEnabled: boolean;
    constructor(object: THREE.Object3D, container: HTMLElement, enabled?: boolean, updateEnabled?: boolean) {
        super();

        if (enabled == undefined) enabled = false;
        if (updateEnabled == undefined) updateEnabled = enabled;

        this.me = new digifie.controls.MouseControls(object, container);
        this._updateEnabled = updateEnabled;
        this.me.enabled = enabled;

        this._type = CameraControlType.Object;
    }

    public get enabled(): boolean { return this.me.enabled; }
    public set enabled(value: boolean) {
        this.me.enabled = value;
        this.me.update();
    }

    public get updateEnabled(): boolean { return this._updateEnabled; }
    public set updateEnabled(value: boolean) {
        this._updateEnabled = value;
    }

    public update(cameras: Cameras, camera: Camera, items: ItemManager, timeStamp: number) {
        if (this.updateEnabled) {
            this.me.update();
            super.update(cameras, camera, items, timeStamp);
        }
    }
    public get orientation(): { x: number, y: number } { return this.me.orientation; }
}

// CameraNavigator 派生クラス.
/* WalkThroughCameraControls
 *　ウォークスルーカメラコントローラ
 */
export class WalkThroughCameraControls extends CameraNavigator {
    _instance: CameraControls;
    public get me(): CameraControls { return this._instance; }
    public set me(i: CameraControls) { this._instance = i; }

    public get enabled(): boolean { return this.me.visible; }
    public set enabled(value: boolean) { this.me.visible = value; }

    constructor(container: HTMLElement, control: WalkThroughCameraControl, eventDispatcher?: events.EventDispatcher) {
        super(eventDispatcher);

        this.me = new CameraControls("camera_controls", 0, 0, PosOrigin.RightBottom, container, control);
        this.me.visible = false;
    }
    public notify(message: string, param: any) {
        ;
    }
}

// CameraNavigator 派生クラス.
/* WalkThroughAnimationControl
 *　ウォークスルーアニメーションコントローラ
 */
export class WalkThroughAnimationControl extends CameraNavigator {
    private _wtAnimations: any;
    private _enabled: boolean;
    private _targets: THREE.Object3D[];
    private _lastChecked: number;
    private _lastPosition: THREE.Vector3;
    private _lastLookAt: THREE.Vector3;
    private _checkInterval: number;

    public get enabled(): boolean { return this._enabled; }
    public set enabled(value: boolean) { this._enabled = value; }
    public getAnimation(name: string) { return this._wtAnimations[name]; }
    public set checkInterval(value: number) { this._checkInterval = value; }

    constructor(eventDispatcher?: events.EventDispatcher) {
        super(eventDispatcher);

        this._wtAnimations = {};
        this._enabled = true;
        this._targets = [];
        this._lastChecked = 0;
        this._lastPosition = new THREE.Vector3(0, 0, 0);
        this._lastLookAt = new THREE.Vector3(0, 0, 0);
        this._checkInterval = 300; //in msec.

        if (eventDispatcher != undefined) {
            eventDispatcher.addEventListener(defines.EventNames.wtObjectClicked, (e: events.Event) => {
                let target: WalkThroughAnimation = this.extractTarget(e.value, TriggerType.clickOn);
                if (target != undefined) this.updateTarget(target, 0/*timeStamp*/);
            });
        }
    }

    public add(wtAnim: WalkThroughAnimation) {
        this._wtAnimations[wtAnim.name] = wtAnim;
    }

    public getTargets(cameras: Cameras, camera: Camera, items: ItemManager): THREE.Object3D[] {
        return undefined;
    }

    protected checkStatus(timeStamp: number, camera: Camera): boolean {
        if ((timeStamp - this._lastChecked > this._checkInterval) && (!this._lastPosition.equals(camera.position) || !this._lastLookAt.equals(camera.lookAt))) {
            this._lastChecked = timeStamp;
            this._lastPosition.copy(camera.position);
            this._lastLookAt.copy(camera.lookAt);
            return true;
        }
        return false;
    }

    protected getTriggerName(object: THREE.Object3D): string {
        return object.name;
    }

    private extractTarget(hitObjects: any[], type: TriggerType): WalkThroughAnimation {
        if (hitObjects.length > 0) {
            let hitLabel = this.getTriggerName(hitObjects[0].object);
            if (hitLabel != undefined) {
                let anim: WalkThroughAnimation = this.getAnimation(hitLabel);
                if ((anim != undefined) && (anim.type == type) && anim.isInRange(hitObjects[0].distance))
                    return anim;
            }
        }
        return undefined;
    }

    public findTarget(targets: THREE.Object3D[], point: THREE.Vector3, dir: THREE.Vector3, type: TriggerType): WalkThroughAnimation {
        if (targets != undefined) {
            let rayCaster = new THREE.Raycaster(point, dir);
            let hitObjects = rayCaster.intersectObjects(targets, true);
            return this.extractTarget(hitObjects, type);
        }
        return undefined;
    }

    public updateTarget(target: WalkThroughAnimation, timeStamp: number): void {
        if (target == undefined) return;
        if (!target.enabled) target.start();
    }

    public update(cameras: Cameras, camera: Camera, items: ItemManager, timeStamp: number): void {
        if (this.checkStatus(timeStamp, camera)) {
            let targets: THREE.Object3D[] = this.getTargets(cameras, camera, items);
            let target: WalkThroughAnimation = this.findTarget(targets, camera.position, camera.lookAt, TriggerType.standBefore);
            if (target != undefined) this.updateTarget(target, timeStamp);
        }
        for (let key in this._wtAnimations) {
            this._wtAnimations[key].update(camera, timeStamp);
        }
    }
}
